import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Plus, Search, Edit, Trash2, FileText, Download,
  Bot, Copy, Eye, Loader2, Link as LinkIcon, ClipboardCopy, ExternalLink
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { buildSafeStoragePath, validateUpload, safeContentType } from "@/lib/sanitizeFilename";
import { useRole } from "@/contexts/RoleContext";

const categories = [
  "Marketing", "Ventas", "Recursos Humanos", "Finanzas", "Operaciones",
  "Atención al Cliente", "Desarrollo de Producto", "Análisis de Datos",
  "Automatización", "General"
];

type ResourceType = "download" | "link" | "copy";

const RESOURCE_TYPES: { value: ResourceType; label: string; hint: string }[] = [
  { value: "download", label: "Descargable", hint: "Archivo para descargar (JSON de n8n, skill de Claude .md, documento...)" },
  { value: "link", label: "Link", hint: "Enlace externo (repositorio de GitHub, página web...)" },
  { value: "copy", label: "Copiar y pegar", hint: "Texto que el participante copia (prompt, instrucciones...)" },
];

const typeLabel = (t: string) => RESOURCE_TYPES.find(r => r.value === t)?.label ?? "Copiar y pegar";

const TypeIcon = ({ type, className = "h-3 w-3" }: { type: string; className?: string }) => {
  if (type === "download") return <Download className={className} />;
  if (type === "link") return <LinkIcon className={className} />;
  return <ClipboardCopy className={className} />;
};

interface CatalogTool {
  id: string;
  name: string;
  logo_url: string | null;
  category: string | null;
}

interface Resource {
  id: string;
  name: string;
  description: string;
  content: string;
  tool_id: string | null;
  category: string;
  tags: string[];
  solution_types: number[];
  resource_type: ResourceType;
  external_url: string | null;
  file_name: string | null;
  file_url: string | null;
  created_at: string;
  updated_at: string;
  usage_count: number;
}

const isValidUrl = (value: string) => {
  try {
    const u = new URL(value);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch { return false; }
};

const ResourceManagementPage = () => {
  const { currentRole } = useRole();
  const [resources, setResources] = useState<Resource[]>([]);
  const [catalogTools, setCatalogTools] = useState<CatalogTool[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedToolId, setSelectedToolId] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedSolutionType, setSelectedSolutionType] = useState("all");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);

  const [formData, setFormData] = useState({
    name: "", description: "", content: "", toolId: "", category: "", tags: "",
    resourceType: "copy" as ResourceType,
    externalUrl: "",
    solutionTypes: [] as number[],
    file: null as File | null
  });

  const SOLUTION_TYPE_OPTIONS = [
    { value: 1, label: 'Contenido IA' },
    { value: 2, label: 'Vibecoding' },
    { value: 3, label: 'Asistente IA' },
    { value: 4, label: 'Automatización' },
    { value: 5, label: 'Análisis de Datos' },
  ];

  const resetForm = () => {
    setFormData({ name: "", description: "", content: "", toolId: "", category: "", tags: "", resourceType: "copy", externalUrl: "", solutionTypes: [], file: null });
  };

  // ---------- FETCH CATALOG TOOLS ----------
  const fetchCatalogTools = useCallback(async () => {
    const { data, error } = await supabase
      .from("ai_tools_catalog")
      .select("id, name, logo_url, category")
      .order("name");
    if (error) {
      console.error("Error fetching catalog tools:", error);
    } else {
      setCatalogTools((data as CatalogTool[]) || []);
    }
  }, []);

  // ---------- FETCH RESOURCES ----------
  const fetchResources = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("templates")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      toast.error("Error al cargar recursos");
      console.error(error);
    } else {
      setResources((data as unknown as Resource[]) || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCatalogTools();
    fetchResources();
  }, [fetchCatalogTools, fetchResources]);

  // ---------- TOOL HELPERS ----------
  const getToolInfo = (toolId: string | null): CatalogTool | null => {
    if (!toolId) return null;
    return catalogTools.find(t => t.id === toolId) || null;
  };

  // ---------- FILE HELPERS ----------
  const uploadFile = async (file: File): Promise<{ file_name: string; file_url: string } | null> => {
    const check = validateUpload(file);
    if (!check.ok) { toast.error(check.error); return null; }
    const path = buildSafeStoragePath("templates", file.name);
    const { error } = await supabase.storage.from("content").upload(path, file, {
      contentType: safeContentType(file),
    });
    if (error) { toast.error("Error al subir archivo"); return null; }
    const { data: urlData } = supabase.storage.from("content").getPublicUrl(path);
    return { file_name: file.name, file_url: urlData.publicUrl };
  };

  const deleteStorageFile = async (fileUrl: string) => {
    try {
      const url = new URL(fileUrl);
      const match = url.pathname.match(/\/object\/public\/content\/(.+)/);
      if (match) {
        await supabase.storage.from("content").remove([match[1]]);
      }
    } catch { /* ignore */ }
  };

  // ---------- VALIDATION BY TYPE ----------
  const validateByType = (isEdit: boolean): string | null => {
    if (formData.resourceType === "link") {
      if (!formData.externalUrl.trim()) return "Ingresa el enlace del recurso";
      if (!isValidUrl(formData.externalUrl.trim())) return "El enlace debe iniciar con http:// o https://";
    }
    if (formData.resourceType === "download") {
      const hasExisting = isEdit && !!selectedResource?.file_url;
      if (!formData.file && !hasExisting) return "Adjunta el archivo descargable";
    }
    if (formData.resourceType === "copy" && !formData.content.trim()) {
      return "Ingresa el contenido para copiar y pegar";
    }
    return null;
  };

  // ---------- CREATE ----------
  const handleCreate = async () => {
    if (!formData.name || !formData.toolId || !formData.category) {
      toast.error("Por favor completa los campos requeridos");
      return;
    }
    const typeError = validateByType(false);
    if (typeError) { toast.error(typeError); return; }

    setSaving(true);
    let fileInfo: { file_name: string; file_url: string } | null = null;
    if (formData.file) {
      fileInfo = await uploadFile(formData.file);
      if (!fileInfo) { setSaving(false); return; }
    }

    const { error } = await supabase.from("templates").insert({
      name: formData.name,
      description: formData.description,
      content: formData.resourceType === "copy" ? formData.content : formData.content || "",
      tool_id: formData.toolId,
      category: formData.category,
      tags: formData.tags.split(",").map(t => t.trim()).filter(Boolean),
      solution_types: formData.solutionTypes,
      resource_type: formData.resourceType,
      external_url: formData.resourceType === "link" ? formData.externalUrl.trim() : null,
      file_name: fileInfo?.file_name ?? null,
      file_url: fileInfo?.file_url ?? null,
    });

    setSaving(false);
    if (error) { toast.error("Error al crear recurso"); console.error(error); return; }
    setIsCreateOpen(false);
    resetForm();
    toast.success("Recurso creado exitosamente");
    fetchResources();
  };

  // ---------- EDIT ----------
  const handleEdit = async () => {
    if (!selectedResource) return;
    const typeError = validateByType(true);
    if (typeError) { toast.error(typeError); return; }
    setSaving(true);

    let fileInfo: { file_name: string; file_url: string } | null = null;
    if (formData.file) {
      if (selectedResource.file_url) await deleteStorageFile(selectedResource.file_url);
      fileInfo = await uploadFile(formData.file);
      if (!fileInfo) { setSaving(false); return; }
    }

    const updates: Record<string, unknown> = {
      name: formData.name,
      description: formData.description,
      content: formData.content,
      tool_id: formData.toolId || null,
      category: formData.category,
      tags: formData.tags.split(",").map(t => t.trim()).filter(Boolean),
      solution_types: formData.solutionTypes,
      resource_type: formData.resourceType,
      external_url: formData.resourceType === "link" ? formData.externalUrl.trim() : null,
    };
    if (fileInfo) {
      updates.file_name = fileInfo.file_name;
      updates.file_url = fileInfo.file_url;
    }

    const { error } = await supabase.from("templates").update(updates).eq("id", selectedResource.id);
    setSaving(false);
    if (error) { toast.error("Error al actualizar recurso"); console.error(error); return; }
    setIsEditOpen(false);
    setSelectedResource(null);
    resetForm();
    toast.success("Recurso actualizado exitosamente");
    fetchResources();
  };

  // ---------- DELETE ----------
  const handleDelete = async (resource: Resource) => {
    if (resource.file_url) await deleteStorageFile(resource.file_url);
    const { error } = await supabase.from("templates").delete().eq("id", resource.id);
    if (error) { toast.error("Error al eliminar recurso"); return; }
    toast.success("Recurso eliminado exitosamente");
    fetchResources();
  };

  // ---------- DUPLICATE ----------
  const handleDuplicate = async (resource: Resource) => {
    const { error } = await supabase.from("templates").insert({
      name: `${resource.name} (copia)`,
      description: resource.description,
      content: resource.content,
      tool_id: resource.tool_id,
      category: resource.category,
      tags: resource.tags,
      solution_types: resource.solution_types,
      resource_type: resource.resource_type,
      external_url: resource.external_url,
      file_name: resource.file_name,
      file_url: resource.file_url,
      usage_count: 0,
    });
    if (error) { toast.error("Error al duplicar recurso"); return; }
    toast.success("Recurso duplicado exitosamente");
    fetchResources();
  };

  // ---------- DOWNLOAD ----------
  const handleDownload = (resource: Resource) => {
    if (resource.file_url) {
      window.open(resource.file_url, "_blank");
    }
  };

  const openEditDialog = (resource: Resource) => {
    setSelectedResource(resource);
    setFormData({
      name: resource.name,
      description: resource.description || "",
      content: resource.content || "",
      toolId: resource.tool_id || "",
      category: resource.category || "",
      tags: (resource.tags || []).join(", "),
      resourceType: (resource.resource_type as ResourceType) || "copy",
      externalUrl: resource.external_url || "",
      solutionTypes: resource.solution_types || [],
      file: null
    });
    setIsEditOpen(true);
  };

  const openViewDialog = (resource: Resource) => {
    setSelectedResource(resource);
    setIsViewOpen(true);
  };

  // Filter resources
  const filteredResources = resources.filter(resource => {
    const matchesSearch = resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (resource.description || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (resource.tags || []).some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesTool = selectedToolId === "all" || resource.tool_id === selectedToolId;
    const matchesCategory = selectedCategory === "all" || resource.category === selectedCategory;
    const matchesType = selectedType === "all" || (resource.resource_type || "copy") === selectedType;
    const matchesSolutionType = selectedSolutionType === "all" ||
      (resource.solution_types || []).includes(Number(selectedSolutionType));
    return matchesSearch && matchesTool && matchesCategory && matchesType && matchesSolutionType;
  });

  const formatDate = (dateStr: string) => {
    try { return new Date(dateStr).toLocaleDateString("es-ES"); } catch { return dateStr; }
  };

  // Stats
  const stats = {
    total: resources.length,
    totalUsage: resources.reduce((acc, t) => acc + (t.usage_count || 0), 0),
  };

  // ---------- TOOL SELECTOR (shared) ----------
  const renderToolSelector = (value: string, onChange: (v: string) => void) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger><SelectValue placeholder="Seleccionar herramienta" /></SelectTrigger>
      <SelectContent className="bg-background border border-border z-50 max-h-[300px]">
        {catalogTools.map(tool => (
          <SelectItem key={tool.id} value={tool.id}>
            <div className="flex items-center gap-2">
              {tool.logo_url ? (
                <img src={tool.logo_url} alt={tool.name} className="w-4 h-4 rounded object-contain" />
              ) : (
                <Bot className="w-4 h-4 text-muted-foreground" />
              )}
              {tool.name}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  // ---------- FORM DIALOG (shared) ----------
  const renderFormFields = () => (
    <div className="space-y-4 py-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Nombre *</Label>
          <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Nombre del recurso" />
        </div>
        <div className="space-y-2">
          <Label>Herramienta *</Label>
          {renderToolSelector(formData.toolId, (v) => setFormData({...formData, toolId: v}))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Tipo de recurso *</Label>
        <Select value={formData.resourceType} onValueChange={(v) => setFormData({...formData, resourceType: v as ResourceType})}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent className="bg-background border border-border z-50">
            {RESOURCE_TYPES.map(rt => (
              <SelectItem key={rt.value} value={rt.value}>
                <div className="flex items-center gap-2"><TypeIcon type={rt.value} className="h-4 w-4" />{rt.label}</div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{RESOURCE_TYPES.find(r => r.value === formData.resourceType)?.hint}</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Categoría *</Label>
          <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
            <SelectTrigger><SelectValue placeholder="Seleccionar categoría" /></SelectTrigger>
            <SelectContent>
              {categories.map(cat => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Etiquetas</Label>
          <Input value={formData.tags} onChange={(e) => setFormData({...formData, tags: e.target.value})} placeholder="etiqueta1, etiqueta2" />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Descripción</Label>
        <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Describe para qué sirve este recurso" rows={2} />
      </div>

      {formData.resourceType === "link" && (
        <div className="space-y-2">
          <Label>Enlace *</Label>
          <Input
            value={formData.externalUrl}
            onChange={(e) => setFormData({...formData, externalUrl: e.target.value})}
            placeholder="https://github.com/usuario/repositorio"
          />
          <p className="text-xs text-muted-foreground">Repositorio de GitHub, documentación o cualquier página pública.</p>
        </div>
      )}

      {formData.resourceType === "download" && (
        <div className="space-y-2">
          <Label>Archivo descargable *</Label>
          <Input type="file" onChange={(e) => setFormData({...formData, file: e.target.files?.[0] || null})} />
          {formData.file && <p className="text-sm text-muted-foreground">Archivo seleccionado: {formData.file.name}</p>}
          {!formData.file && selectedResource?.file_name && isEditOpen && (
            <p className="text-sm text-muted-foreground">Archivo actual: {selectedResource.file_name}</p>
          )}
          <p className="text-xs text-muted-foreground">Ej.: flujo de n8n (.json), skill de Claude (.md), documento o plantilla.</p>
        </div>
      )}

      <div className="space-y-2">
        <Label>{formData.resourceType === "copy" ? "Contenido para copiar *" : "Instrucciones de uso (opcional)"}</Label>
        <Textarea
          value={formData.content}
          onChange={(e) => setFormData({...formData, content: e.target.value})}
          placeholder={formData.resourceType === "copy" ? "Prompt, código o instrucciones que el participante copiará" : "Cómo usar este recurso paso a paso"}
          rows={formData.resourceType === "copy" ? 8 : 4}
          className="font-mono text-sm"
        />
      </div>

      <div className="space-y-2">
        <Label>Tipos de Proyecto</Label>
        <div className="flex flex-wrap gap-2">
          {SOLUTION_TYPE_OPTIONS.map(opt => {
            const selected = formData.solutionTypes.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setFormData(prev => ({
                  ...prev,
                  solutionTypes: selected
                    ? prev.solutionTypes.filter(v => v !== opt.value)
                    : [...prev.solutionTypes, opt.value]
                }))}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  selected ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted text-muted-foreground border-border hover:border-primary/50'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">Selecciona los tipos de proyecto para los que aplica este recurso</p>
      </div>
    </div>
  );

  // Guard de rol en el cuerpo (defensa en profundidad, además del AdminRoute).
  if (currentRole !== "admin" && currentRole !== "super_admin") {
    return (
      <div className="p-8 text-center">
        <h1 className="text-2xl font-bold text-destructive">Acceso Denegado</h1>
        <p className="text-muted-foreground mt-2">Solo los administradores pueden acceder a esta página.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Gestión de Recursos</h1>
          <p className="text-muted-foreground">Administra recursos descargables, enlaces y contenido para copiar y pegar</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => resetForm()}><Plus className="h-4 w-4 mr-2" />Nuevo Recurso</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Crear Nuevo Recurso</DialogTitle></DialogHeader>
            {renderFormFields()}
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancelar</Button>
              <Button onClick={handleCreate} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Crear Recurso
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4">
        <Card><CardContent className="pt-4"><div className="text-2xl font-bold">{stats.total}</div><p className="text-sm text-muted-foreground">Total Recursos</p></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="text-2xl font-bold">{stats.totalUsage}</div><p className="text-sm text-muted-foreground">Usos Totales</p></CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar recursos..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" />
        </div>
        <Select value={selectedType} onValueChange={setSelectedType}>
          <SelectTrigger className="w-[190px]"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent className="bg-background border border-border z-50">
            <SelectItem value="all">Todos los tipos</SelectItem>
            {RESOURCE_TYPES.map(rt => (<SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={selectedToolId} onValueChange={setSelectedToolId}>
          <SelectTrigger className="w-[200px]"><SelectValue placeholder="Herramienta" /></SelectTrigger>
          <SelectContent className="bg-background border border-border z-50 max-h-[300px]">
            <SelectItem value="all">Todas las herramientas</SelectItem>
            {catalogTools.map(tool => (
              <SelectItem key={tool.id} value={tool.id}>
                <div className="flex items-center gap-2">
                  {tool.logo_url ? (
                    <img src={tool.logo_url} alt={tool.name} className="w-4 h-4 rounded object-contain" />
                  ) : (
                    <Bot className="w-4 h-4 text-muted-foreground" />
                  )}
                  {tool.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Categoría" /></SelectTrigger>
          <SelectContent className="bg-background border border-border z-50">
            <SelectItem value="all">Todas las categorías</SelectItem>
            {categories.map(cat => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={selectedSolutionType} onValueChange={setSelectedSolutionType}>
          <SelectTrigger className="w-[190px]"><SelectValue placeholder="Tipo de proyecto" /></SelectTrigger>
          <SelectContent className="bg-background border border-border z-50">
            <SelectItem value="all">Todos los tipos</SelectItem>
            {SOLUTION_TYPE_OPTIONS.map(opt => (<SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {/* Resources Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Recurso</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Herramienta</TableHead>
                  <TableHead>Categoría</TableHead>
                  <TableHead>Tipos de Proyecto</TableHead>
                  <TableHead>Etiquetas</TableHead>
                  <TableHead className="text-center">Usos</TableHead>
                  <TableHead>Actualizado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredResources.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No se encontraron recursos</TableCell>
                  </TableRow>
                ) : (
                  filteredResources.map(resource => {
                    const toolInfo = getToolInfo(resource.tool_id);
                    return (
                      <TableRow key={resource.id}>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="font-medium">{resource.name}</div>
                            <div className="text-sm text-muted-foreground line-clamp-1">{resource.description}</div>
                            {resource.file_name && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground"><FileText className="h-3 w-3" />{resource.file_name}</div>
                            )}
                            {resource.external_url && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground truncate max-w-[280px]"><LinkIcon className="h-3 w-3" />{resource.external_url}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="gap-1">
                            <TypeIcon type={resource.resource_type || "copy"} />
                            {typeLabel(resource.resource_type || "copy")}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {toolInfo ? (
                            <Badge variant="outline" className="gap-1">
                              {toolInfo.logo_url ? (
                                <img src={toolInfo.logo_url} alt={toolInfo.name} className="w-3 h-3 rounded object-contain" />
                              ) : (
                                <Bot className="w-3 h-3" />
                              )}
                              {toolInfo.name}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell>{resource.category}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(resource.solution_types || []).slice(0, 2).map(st => {
                              const label = SOLUTION_TYPE_OPTIONS.find(o => o.value === st)?.label;
                              return label ? <Badge key={st} variant="secondary" className="text-xs">{label}</Badge> : null;
                            })}
                            {(resource.solution_types || []).length > 2 && (
                              <Badge variant="secondary" className="text-xs">+{(resource.solution_types || []).length - 2}</Badge>
                            )}
                            {(resource.solution_types || []).length === 0 && <span className="text-muted-foreground text-sm">—</span>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(resource.tags || []).slice(0, 2).map(tag => (<Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>))}
                            {(resource.tags || []).length > 2 && <Badge variant="secondary" className="text-xs">+{resource.tags.length - 2}</Badge>}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">{resource.usage_count}</TableCell>
                        <TableCell>{formatDate(resource.updated_at)}</TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openViewDialog(resource)}><Eye className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => openEditDialog(resource)}><Edit className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDuplicate(resource)}><Copy className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" onClick={() => handleDelete(resource)}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Editar Recurso</DialogTitle></DialogHeader>
          {renderFormFields()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancelar</Button>
            <Button onClick={handleEdit} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{selectedResource?.name}</DialogTitle></DialogHeader>
          {selectedResource && (
            <div className="space-y-4 py-4">
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="gap-1">
                  <TypeIcon type={selectedResource.resource_type || "copy"} />
                  {typeLabel(selectedResource.resource_type || "copy")}
                </Badge>
                {(() => {
                  const toolInfo = getToolInfo(selectedResource.tool_id);
                  return toolInfo ? (
                    <Badge variant="outline" className="gap-1">
                      {toolInfo.logo_url ? (
                        <img src={toolInfo.logo_url} alt={toolInfo.name} className="w-3 h-3 rounded object-contain" />
                      ) : (
                        <Bot className="w-3 h-3" />
                      )}
                      {toolInfo.name}
                    </Badge>
                  ) : null;
                })()}
                <Badge variant="secondary">{selectedResource.category}</Badge>
                {(selectedResource.tags || []).map(tag => (<Badge key={tag} variant="outline">{tag}</Badge>))}
              </div>
              <p className="text-muted-foreground">{selectedResource.description}</p>
              {selectedResource.content && (
                <div className="space-y-2">
                  <Label>Contenido</Label>
                  <div className="bg-muted p-4 rounded-lg font-mono text-sm whitespace-pre-wrap overflow-x-auto">{selectedResource.content}</div>
                </div>
              )}
              {selectedResource.external_url && (
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  <LinkIcon className="h-5 w-5 text-muted-foreground" />
                  <span className="flex-1 truncate">{selectedResource.external_url}</span>
                  <Button variant="outline" size="sm" onClick={() => window.open(selectedResource.external_url!, "_blank")}>
                    <ExternalLink className="h-4 w-4 mr-2" />Abrir
                  </Button>
                </div>
              )}
              {selectedResource.file_name && (
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <span className="flex-1">{selectedResource.file_name}</span>
                  <Button variant="outline" size="sm" onClick={() => handleDownload(selectedResource)}>
                    <Download className="h-4 w-4 mr-2" />Descargar
                  </Button>
                </div>
              )}
              <div className="flex gap-4 text-sm text-muted-foreground">
                <span>Creado: {formatDate(selectedResource.created_at)}</span>
                <span>Actualizado: {formatDate(selectedResource.updated_at)}</span>
                <span>Usos: {selectedResource.usage_count}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => navigator.clipboard.writeText(selectedResource?.content || "").then(() => toast.success("Contenido copiado"))}>
              <Copy className="h-4 w-4 mr-2" />Copiar Contenido
            </Button>
            <Button onClick={() => { setIsViewOpen(false); if (selectedResource) openEditDialog(selectedResource); }}>
              <Edit className="h-4 w-4 mr-2" />Editar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResourceManagementPage;
